<template>
    <div class="audit-log-main">
        <!-- 통계 (컴팩트) -->
        <div v-if="statistics" class="stats-inline">
            <span class="stats-total"
                >전체 <strong>{{ statistics.totalCount }}</strong
                >건</span
            >
            <span v-for="e in (statistics.byEntity || []).slice(0, 5)" :key="e.entity" class="stats-item">
                <span class="stats-sep">|</span>
                <span class="stats-entity"
                    >{{ getEntityLabel(e.entity) }} <strong>{{ e.count }}</strong></span
                >
            </span>
        </div>

        <!-- 필터 + 목록 (ActivityConfig 스타일 통합) -->
        <AuditLogFilters
            v-model="filters"
            :entity-options="entityOptions"
            :action-options="actionOptions"
            @apply="onFilterApply"
        />
        <AuditLogTable
            :logs="logs"
            :loading="loading"
            :entity-label-map="entityLabelMap"
            :action-label-map="actionLabelMap"
            @row-click="openDetail"
        />

        <!-- 페이징 -->
        <TablePagination
            v-if="pagination.totalCount > 0"
            :total-items="pagination.totalCount"
            :current-page="pagination.page"
            :page-size="pageSize"
            :page-size-options="[10, 20, 50, 100]"
            @update:currentPage="goPage($event)"
            @update:pageSize="onPageSizeChangeFromChild($event)"
        />

        <!-- 상세 모달 -->
        <AuditLogDetailModal
            :visible="detailVisible"
            :detail="detailData"
            :loading="detailLoading"
            :error="detailError"
            @close="detailVisible = false"
            @show-entity-history="openEntityHistory"
        />

        <!-- 엔티티 이력 모달 -->
        <div v-if="entityHistoryVisible" class="audit-detail-overlay" @click.self="entityHistoryVisible = false">
            <div class="audit-detail-modal entity-history-modal">
                <div class="modal-header">
                    <h3>엔티티 변경 이력 — {{ entityHistoryEntity }} #{{ entityHistoryEntityIdx }}</h3>
                    <button type="button" class="btn-close" @click="entityHistoryVisible = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="entityHistoryLoading" class="loading">로딩 중...</div>
                    <div v-else-if="entityHistoryList.length === 0">이력이 없습니다.</div>
                    <ul v-else class="history-list">
                        <li
                            v-for="log in entityHistoryList"
                            :key="log.logIdx"
                            class="history-item"
                            @click="openDetailByLog(log)"
                        >
                            <span class="hist-date">{{ formatDate(log.actionDate) }}</span>
                            <span class="action-badge" :class="getActionClass(log.action)">{{
                                getActionLabel(log.action)
                            }}</span>
                            {{ log.worker || "-" }}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import carbonApi from "../../../api";
import CarbonCodes from "../../../codes/carbonCodes";
import AuditLogFilters from "../views/AuditLogFilters.vue";
import AuditLogTable from "../views/AuditLogTable.vue";
import AuditLogDetailModal from "../modals/AuditLogDetailModal.vue";
import TablePagination from "../../common/TablePagination.vue";

export default {
    name: "AuditLogMain",
    components: {
        AuditLogFilters,
        AuditLogTable,
        AuditLogDetailModal,
        TablePagination,
    },
    props: {
        loginUser: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            filters: {},
            sortBy: "regDate",
            sortOrder: "DESC",
            pageSize: 10,
            logs: [],
            pagination: {
                page: 1,
                pageSize: 10,
                totalCount: 0,
                totalPages: 0,
            },
            loading: false,
            statistics: null,
            detailVisible: false,
            detailData: null,
            detailLoading: false,
            detailError: "",
            entityHistoryVisible: false,
            entityHistoryEntity: "",
            entityHistoryEntityIdx: null,
            entityHistoryList: [],
            entityHistoryLoading: false,
        };
    },
    computed: {
        // 공통코드는 CarbonCodes가 로딩 완료 시점에 반응형 플래그를 갱신하도록 되어 있으므로
        // mounted에서 스냅샷으로 박아둘 필요가 없습니다.
        entityOptions() {
            return CarbonCodes.auditEntity.getList();
        },
        actionOptions() {
            return CarbonCodes.auditAction.getList();
        },
        entityLabelMap() {
            return this.buildLabelMap(this.entityOptions);
        },
        actionLabelMap() {
            return this.buildLabelMap(this.actionOptions);
        },
    },
    async mounted() {
        this.fetchLogs();
        this.fetchStatistics();
    },
    methods: {
        buildLabelMap(list) {
            const m = {};
            (list || []).forEach((c) => {
                m[c.value] = c.text;
            });
            return m;
        },
        getEntityLabel(code) {
            return this.entityLabelMap[code] || code || "-";
        },
        getActionLabel(code) {
            return this.actionLabelMap[code] || code || "-";
        },
        getActionClass(code) {
            const meta = CarbonCodes.auditAction.getMeta(code) || {};
            return `action-${meta.color || "default"}`;
        },
        formatDate(val) {
            if (!val) return "-";
            const d = typeof val === "string" ? new Date(val) : val;
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleString("ko-KR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        onFilterApply(appliedFilters) {
            if (appliedFilters !== undefined && appliedFilters !== null) {
                this.filters = { ...appliedFilters };
            }
            this.pagination.page = 1;
            this.fetchLogs();
            this.fetchStatistics();
        },
        async fetchLogs() {
            this.loading = true;
            try {
                const params = {
                    ...this.filters,
                    page: this.pagination.page,
                    pageSize: this.pageSize,
                    sortBy: this.sortBy,
                    sortOrder: this.sortOrder,
                };
                const res = await carbonApi.auditLog.getLogs(params);
                const data = res?.data?.data ?? res?.data ?? res;
                this.logs = data.logs || [];
                this.pagination = {
                    page: data.pagination?.page ?? this.pagination.page,
                    pageSize: data.pagination?.pageSize ?? this.pageSize,
                    totalCount: data.pagination?.totalCount ?? 0,
                    totalPages: data.pagination?.totalPages ?? 0,
                };
            } catch (err) {
                console.error("감사 로그 목록 조회 실패:", err);
                this.logs = [];
                const msg = err?.response?.data?.message || err?.message || "목록 조회에 실패했습니다.";
                if (this.$bvToast?.toast) {
                    this.$bvToast.toast(msg, { variant: "danger", title: "오류" });
                } else {
                    alert(msg);
                }
            } finally {
                this.loading = false;
            }
        },
        async fetchStatistics() {
            try {
                const params = {};
                if (this.filters.startDate) params.startDate = this.filters.startDate;
                if (this.filters.endDate) params.endDate = this.filters.endDate;
                const res = await carbonApi.auditLog.getStatistics(params);
                const data = res?.data?.data ?? res?.data ?? res;
                this.statistics = data;
            } catch {
                this.statistics = null;
            }
        },
        async openDetail(row) {
            this.detailVisible = true;
            this.detailData = null;
            this.detailError = "";
            this.detailLoading = true;
            try {
                const res = await carbonApi.auditLog.getLogByIdx(row.logIdx);
                const data = res?.data?.data ?? res?.data ?? res;
                this.detailData = data;
            } catch (err) {
                this.detailError = err?.response?.data?.message || "상세 조회에 실패했습니다.";
            } finally {
                this.detailLoading = false;
            }
        },
        openDetailByLog(log) {
            this.entityHistoryVisible = false;
            this.detailData = log;
            this.detailVisible = true;
            this.detailLoading = false;
            this.detailError = "";
        },
        async openEntityHistory(detail) {
            if (!detail || !detail.entity || detail.entityIdx == null) return;
            this.entityHistoryVisible = true;
            this.entityHistoryEntity = detail.entity;
            this.entityHistoryEntityIdx = detail.entityIdx;
            this.entityHistoryList = [];
            this.entityHistoryLoading = true;
            try {
                const res = await carbonApi.auditLog.getEntityHistory(detail.entity, detail.entityIdx);
                const data = res?.data?.data ?? res?.data ?? res;
                this.entityHistoryList = data.logs || [];
            } catch {
                this.entityHistoryList = [];
            } finally {
                this.entityHistoryLoading = false;
            }
        },
        goPage(page) {
            this.pagination.page = page;
            this.fetchLogs();
        },
        onPageSizeChangeFromChild(newSize) {
            this.pageSize = newSize;
            this.pagination.page = 1;
            this.pagination.pageSize = newSize;
            /* fetchLogs는 자식이 emit하는 update:currentPage(1) → goPage(1)에서 호출됨 */
        },
    },
};
</script>

<style scoped>
.audit-log-main {
    padding: 10px 16px;
    min-height: 200px;
}
.stats-inline {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #e2e8f0;
}
.stats-inline strong {
    color: #1e293b;
    font-weight: 600;
}
.stats-item {
    display: inline;
}
.stats-sep {
    margin: 0 8px;
    color: #cbd5e1;
}
.stats-entity {
    margin-right: 4px;
}
.stats-total {
    margin-right: 4px;
}
.audit-detail-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    padding: 20px;
}
.audit-detail-modal {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    max-width: 480px;
    width: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}
.entity-history-modal {
    max-width: 520px;
}
.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 12px 12px 0 0;
}
.modal-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
}
.btn-close {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #64748b;
    border-radius: 6px;
    cursor: pointer;
}
.modal-body {
    padding: 20px;
    overflow-y: auto;
}
.history-list {
    list-style: none;
    padding: 0;
    margin: 0;
}
.history-item {
    padding: 10px 12px;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
}
.history-item:hover {
    background: #f8fafc;
}
.hist-date {
    min-width: 140px;
    color: #64748b;
}
.action-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
}
.action-green {
    background: #dcfce7;
    color: #166534;
}
.action-blue {
    background: #dbeafe;
    color: #1e40af;
}
.action-red {
    background: #fee2e2;
    color: #b91c1c;
}
.action-default {
    background: #f1f5f9;
    color: #475569;
}
.action-purple {
    background: #f3e8ff;
    color: #6b21a8;
}
.action-orange {
    background: #ffedd5;
    color: #c2410c;
}
.loading {
    text-align: center;
    color: #64748b;
    padding: 20px;
}
</style>
